import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import MonitoringFilters, {
  MonitoringFilterScope,
  useMonitoringFilterQuery,
} from '../../components/workforceMonitoring/MonitoringFilters';
import SortableHeader from '../../components/workforceMonitoring/SortableHeader';
import ComputerActivityTimeline from '../../components/workforceMonitoring/ComputerActivityTimeline';
import ActivitySnapshotModal from '../../components/workforceMonitoring/ActivitySnapshotModal';
import { actionDescriptionFromEvent } from '../../utils/actionDescription';
import { SortDir, toggleSort } from '../../utils/tableSort';

const DISPLAY_TZ = 'America/Chicago';

const EVENT_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
  CLOCK_IN: 'success',
  CLOCK_OUT: 'default',
  BROWSER_TAB: 'info',
  FOREGROUND_APP: 'primary',
  APPLICATION: 'primary',
  IDLE_STARTED: 'warning',
  IDLE_DETECTED: 'warning',
  WORKSTATION_LOCKED: 'warning',
  WORK_RESUMED: 'success',
  LUNCH_START: 'info',
  EMERGENCY_BYPASS_GRANTED: 'error',
};

function detailText(event: any): string {
  const { context } = actionDescriptionFromEvent(event);
  if (context) return context;
  return event.detail?.page_title
    || event.detail?.window_title
    || event.detail?.domain
    || event.detail?.application_name
    || '—';
}

function rawActionLines(event: any): string[] {
  const detail = event.detail || {};
  return [
    event.event_type ? `event_type: ${event.event_type}` : null,
    event.source ? `source: ${event.source}` : null,
    detail.process_name ? `process: ${detail.process_name}` : null,
    detail.application_name ? `application: ${detail.application_name}` : null,
    detail.ui_element_type ? `ui_element_type: ${detail.ui_element_type}` : null,
    detail.ui_element_name ? `ui_element_name: ${detail.ui_element_name}` : null,
    detail.action_name ? `action_name: ${detail.action_name}` : null,
    detail.classification ? `classification: ${detail.classification}` : null,
    detail.monitor_id ? `monitor_id: ${detail.monitor_id}` : null,
  ].filter(Boolean) as string[];
}

export interface ActivityPageProps {
  scope?: MonitoringFilterScope;
  lockedDeviceId?: string;
}

const ActivityPage: React.FC<ActivityPageProps> = ({
  scope = 'global',
  lockedDeviceId,
}) => {
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canViewSnapshots = permissions.includes('monitoring.action_snapshots.view');

  const [filters, setFilters] = useMonitoringFilterQuery(scope, lockedDeviceId);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(scope === 'computer' ? 100 : 50);
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [includeDiagnostics, setIncludeDiagnostics] = useState(false);
  const [showRawActions, setShowRawActions] = useState(false);
  const [snapshotModal, setSnapshotModal] = useState<{ id: string; capturedAt?: string | null } | null>(null);

  const hasSnapshotParam = filters.hasSnapshot === 'yes'
    ? true
    : filters.hasSnapshot === 'no'
      ? false
      : undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['monitoringActivity', scope, filters, page, rowsPerPage, sortBy, sortDir, includeDiagnostics, hasSnapshotParam],
    queryFn: () => apiClient.monitoring.getActivity({
      date: filters.date,
      employee_id: filters.employeeId || undefined,
      device_id: filters.deviceId || undefined,
      campaign_id: filters.campaignId || undefined,
      event_type: filters.eventType || undefined,
      has_snapshot: hasSnapshotParam,
      page_size: rowsPerPage,
      page: page + 1,
      sort_by: sortBy,
      sort_dir: sortDir.toUpperCase(),
      feed: includeDiagnostics || filters.eventType ? 'all' : 'computer',
    }),
  });

  const payload = data?.data?.data;
  const events = payload?.events || [];
  const total = payload?.total || 0;
  const feedIsComputer = !(includeDiagnostics || filters.eventType);
  const emptyMessage = feedIsComputer
    ? 'No computer activity recorded for this date. If the agent is online, check Computer Diagnostics → Activity Capture. Enable Include agent diagnostics to see login-gate events.'
    : scope === 'computer'
      ? 'No computer activity was recorded for this computer for the selected date.'
      : 'No activity was recorded for the selected date and filters.';

  const onSort = (column: string) => {
    const next = toggleSort(sortBy, sortDir, column);
    setSortBy(next.key);
    setSortDir(next.dir);
    setPage(0);
  };

  const openSnapshot = (event: any) => {
    const snapshotId = event.snapshot_id || event.detail?.snapshot_id;
    if (!snapshotId) return;
    setSnapshotModal({ id: snapshotId, capturedAt: event.timestamp });
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {scope === 'computer'
          ? 'Computer activity for this machine — applications, window titles, websites, and recorded actions in one chronological feed (newest first). Agent login-gate events are hidden unless you include diagnostics.'
          : 'Activity across computers — applications, websites, idle, work events, and recorded actions. Newest first. Applications and Web Activity tabs roll up time spent.'}
      </Typography>
      <MonitoringFilters
        scope={scope}
        value={filters}
        onChange={(next) => {
          setFilters(next);
          setPage(0);
        }}
        lockedDeviceId={lockedDeviceId}
        showEventType
        showHasSnapshot={canViewSnapshots}
      />
      <Box display="flex" flexWrap="wrap" gap={2} mb={1}>
        <FormControlLabel
          control={(
            <Switch
              size="small"
              checked={includeDiagnostics}
              onChange={(_, checked) => {
                setIncludeDiagnostics(checked);
                setPage(0);
              }}
            />
          )}
          label="Include agent diagnostics (login gate, companion)"
        />
        <FormControlLabel
          control={(
            <Switch
              size="small"
              checked={showRawActions}
              onChange={(_, checked) => setShowRawActions(checked)}
            />
          )}
          label="Show raw action details"
        />
      </Box>
      {isLoading ? (
        <CircularProgress size={20} />
      ) : events.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {emptyMessage}
        </Typography>
      ) : scope === 'computer' ? (
        <Paper sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">
              Activity — {moment(filters.date).format('MMM D, YYYY')}
            </Typography>
            <Chip
              size="small"
              clickable
              label={sortDir === 'desc' ? 'Time DESC' : 'Time ASC'}
              onClick={() => onSort('timestamp')}
            />
          </Box>
          <ComputerActivityTimeline
            events={events}
            total={total}
            showRawActions={showRawActions}
            canViewSnapshots={canViewSnapshots}
            onViewSnapshot={openSnapshot}
          />
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[50, 100, 200, 500]}
          />
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortableHeader label="Time" column="timestamp" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
                  <SortableHeader label="Employee" column="employee_name" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
                  <SortableHeader label="Computer" column="computer" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
                  <SortableHeader label="Campaign" column="campaign" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
                  <SortableHeader label="Type" column="event_type" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
                  <SortableHeader label="Action" column="label" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
                  <TableCell>Context</TableCell>
                  <TableCell>Evidence</TableCell>
                  <SortableHeader label="Duration" column="duration_seconds" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((event: any) => {
                  const { label, context } = actionDescriptionFromEvent(event);
                  const snapshotId = event.snapshot_id || event.detail?.snapshot_id;
                  const hasSnapshot = Boolean(event.has_snapshot || snapshotId);
                  const rawLines = rawActionLines(event);
                  return (
                    <React.Fragment key={`${event.source}-${event.id}`}>
                      <TableRow>
                        <TableCell>{moment(event.timestamp).tz(DISPLAY_TZ).format('h:mm:ss A')}</TableCell>
                        <TableCell>{event.employee_name || '—'}</TableCell>
                        <TableCell>{event.computer || '—'}</TableCell>
                        <TableCell>{event.campaign || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" color={EVENT_COLORS[event.event_type] || 'default'} label={event.event_type} />
                        </TableCell>
                        <TableCell>{label}</TableCell>
                        <TableCell>
                          <Typography variant="caption" component="div">
                            {context || detailText(event)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {canViewSnapshots && hasSnapshot ? (
                            <Button size="small" variant="outlined" onClick={() => openSnapshot(event)}>
                              View Snapshot
                            </Button>
                          ) : '—'}
                        </TableCell>
                        <TableCell>{event.duration_label || '—'}</TableCell>
                      </TableRow>
                      {showRawActions && rawLines.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={9} sx={{ py: 0.5, bgcolor: 'grey.50' }}>
                            <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                              {rawLines.join(' · ')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </Paper>
      )}

      <ActivitySnapshotModal
        open={Boolean(snapshotModal)}
        onClose={() => setSnapshotModal(null)}
        snapshotId={snapshotModal?.id || null}
        capturedAt={snapshotModal?.capturedAt}
      />
    </Box>
  );
};

export default ActivityPage;
